/**
 * patientUpdates — in-app simulated "patient came back" notifications.
 *
 * When the learner finishes an engine case, a Run-the-Shift, or a sim,
 * we drop a scheduled "Patient update" into the queue (cooldown 24-72h
 * by simulated time, but immediate on next site visit so demos feel
 * alive). On every site load we surface ONE pending update as a soft
 * toast on the home/audience pages.
 *
 * Pure client-side: localStorage queue, no backend.
 */
const KEY = "maldek_patient_updates_v1";

/**
 * Pull the queue. Each entry: { id, source, patient, body, created_at, shown_at? }
 */
export function getQueue() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    return JSON.parse(raw) || [];
  } catch {
    return [];
  }
}

function setQueue(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(-20))); // bound the queue
  } catch { /* ignore */ }
}

/**
 * Push a new patient update. The `source` is a free-form tag (engine_case_id,
 * shift_id, etc.) used to de-dupe.
 */
export function enqueueUpdate(update) {
  if (!update?.body) return;
  const list = getQueue();
  // Skip dupes by source.
  if (update.source && list.some((u) => u.source === update.source)) return;
  list.push({
    id: `pu_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    source: update.source || null,
    patient: update.patient || "Your patient",
    body: update.body,
    tone: update.tone || "info", // 'info' | 'good' | 'warn'
    created_at: new Date().toISOString(),
    shown_at: null,
  });
  setQueue(list);
}

/**
 * Return the next unseen update without marking it. Caller marks it via
 * markSeen() after the toast is rendered/dismissed.
 */
export function peekNextUnseen() {
  return getQueue().find((u) => !u.shown_at) || null;
}

export function markSeen(id) {
  const list = getQueue();
  const i = list.findIndex((u) => u.id === id);
  if (i < 0) return;
  list[i].shown_at = new Date().toISOString();
  setQueue(list);
}

/**
 * Seed a deterministic patient-update body for a known engine case id.
 * Kept as a small catalog so updates feel grounded in the actual case
 * the learner just solved.
 */
const CASE_UPDATES = {
  "sepsis-postop": {
    patient: "Mrs. Alvarez",
    body: "24h later — lactate cleared to 1.2, MAP holding 78 on norepi. She's awake and oriented. The night team thanks you for the early bundle.",
    tone: "good",
  },
  "chest-pain-er": {
    patient: "Mr. Chen",
    body: "Cath lab found a 95% LAD lesion. Stented. Troponin peaked at 18, now downtrending. He asked who 'caught it' from the ED chair.",
    tone: "good",
  },
  "stroke-window": {
    patient: "Ms. Park",
    body: "Got tPA at 10:42. NIHSS dropped from 9 → 3 by 24h. Family came in this morning asking how she's talking again.",
    tone: "good",
  },
  "preeclampsia-hellp": {
    patient: "Mrs. Diallo",
    body: "Mag drip running, BP 148/92. Hepatic enzymes plateaued. OB is talking about delivery within 24h.",
    tone: "info",
  },
  "peds-dka": {
    patient: "Liam (8 y/o)",
    body: "Insulin gtt cut the gap from 22 → 12 overnight. Mom asked if the fluids you ordered were what 'kept his sugar from crashing'.",
    tone: "good",
  },
  "urosepsis-stone": {
    patient: "Mr. Park",
    body: "Urology placed a stent at 03:00. Fevers broke. Lactate cleared. He'll go home with PO antibiotics — he wouldn't have without the early call.",
    tone: "good",
  },
  "ovarian-torsion": {
    patient: "Sarah",
    body: "OR found a viable ovary detorsed within the salvage window. She'll keep her fertility because of how fast you escalated.",
    tone: "good",
  },
};

/**
 * Call this from the Reasoning Engine after a case completes successfully.
 * If the case_id has a curated update we enqueue it; otherwise we synthesize
 * a generic "patient stable, follow-up tomorrow" message.
 */
export function recordEngineCompletion({ engine_id, case_id, score }) {
  if (!engine_id) return;
  const curated = case_id ? CASE_UPDATES[case_id] : null;
  if (curated) {
    enqueueUpdate({
      source: `engine:${engine_id}`,
      patient: curated.patient,
      body: curated.body,
      tone: curated.tone,
    });
    return;
  }
  // Generic fallback — score-aware.
  const tone = score >= 80 ? "good" : score >= 60 ? "info" : "warn";
  const body =
    score >= 80
      ? "Your patient came back for follow-up today. Vitals stable, no surprises. Your reasoning held up overnight."
      : score >= 60
      ? "Your patient is still admitted. The team adjusted one part of your plan — see how they did it."
      : "Your patient deteriorated overnight and needed escalation. The team is reviewing what was missed.";
  enqueueUpdate({
    source: `engine:${engine_id}`,
    patient: "Your patient",
    body,
    tone,
  });
}

/**
 * Call this from Run-the-Shift after the shift completes. Produces ONE
 * cohort-level update — e.g. "the four patients you saw checked out fine".
 */
export function recordShiftCompletion({ shift_id, shift_total }) {
  if (!shift_id) return;
  const tone = shift_total >= 75 ? "good" : shift_total >= 50 ? "info" : "warn";
  const body =
    shift_total >= 75
      ? "All four patients made it through the night. The charge nurse left a note: 'whoever ran this shift kept us out of the ICU twice'."
      : shift_total >= 50
      ? "Three of your four patients are stable in the morning. One had to be transferred for closer monitoring."
      : "The morning team rounded on your shift. Two patients escalated overnight — there's a debrief on your dashboard.";
  enqueueUpdate({
    source: `shift:${shift_id}`,
    patient: "Your shift",
    body,
    tone,
  });
}
